import React from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../shared/components';
import { useToast } from '../../notifications';
import {
  createKnowledgeEvaluationCase,
  deleteKnowledgeEvaluationCase,
  listKnowledgeDocuments,
  listKnowledgeEvaluationCases,
  listKnowledgeEvaluationRuns,
  runKnowledgeEvaluation,
  updateKnowledgeEvaluationCase,
} from '../services/knowledgeService';
import type {
  KnowledgeDocument,
  KnowledgeEvaluationCase,
  KnowledgeEvaluationRun,
  KnowledgeEvaluationVariantMetrics,
  UpsertKnowledgeEvaluationCaseRequest,
} from '../types/Knowledge';
import styles from './KnowledgeEvaluationPanel.module.css';

const MAX_EXPECTED_DOCUMENTS = 20;
const DOCUMENT_PICKER_PAGE_SIZE = 100;

type CaseFormState = {
  question: string;
  expectedDocumentIds: string[];
  expectedText: string;
  notes: string;
  isActive: boolean;
};

const emptyForm: CaseFormState = {
  question: '',
  expectedDocumentIds: [],
  expectedText: '',
  notes: '',
  isActive: true,
};

const formatPercent = (value: number): string => `${(value * 100).toFixed(1)}%`;
const formatScore = (value: number): string => value.toFixed(3);
const formatNumber = (value: number): string => new Intl.NumberFormat().format(Math.round(value));

const formatDateTime = (value: string | null): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const toForm = (item: KnowledgeEvaluationCase): CaseFormState => ({
  question: item.question,
  expectedDocumentIds: [...item.expectedDocumentIds],
  expectedText: item.expectedText ?? '',
  notes: item.notes ?? '',
  isActive: item.isActive,
});

const toRequest = (form: CaseFormState): UpsertKnowledgeEvaluationCaseRequest => ({
  question: form.question.trim(),
  expectedDocumentIds: form.expectedDocumentIds,
  expectedText: form.expectedText.trim() ? form.expectedText.trim() : null,
  notes: form.notes.trim() ? form.notes.trim() : null,
  isActive: form.isActive,
});

type MetricKey = 'recallAtK' | 'hitRate' | 'mrr' | 'emptyRate' | 'averageLatencyMilliseconds' | 'p95LatencyMilliseconds';

const bestValueFor = (
  variants: KnowledgeEvaluationVariantMetrics[],
  key: MetricKey,
  higherIsBetter: boolean,
): number | null => {
  if (variants.length < 2) {
    return null;
  }
  const values = variants.map(variant => variant[key]);
  return higherIsBetter ? Math.max(...values) : Math.min(...values);
};

export const KnowledgeEvaluationPanel: React.FC = () => {
  const { t } = useTranslation('knowledge');
  const { showToast } = useToast();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [cases, setCases] = React.useState<KnowledgeEvaluationCase[]>([]);
  const [runs, setRuns] = React.useState<KnowledgeEvaluationRun[]>([]);
  const [documents, setDocuments] = React.useState<KnowledgeDocument[]>([]);
  const [documentSearch, setDocumentSearch] = React.useState('');
  const [running, setRunning] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [editingCaseId, setEditingCaseId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<CaseFormState | null>(null);
  const [showCaseDetails, setShowCaseDetails] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextCases, nextRuns] = await Promise.all([
        listKnowledgeEvaluationCases(),
        listKnowledgeEvaluationRuns(5),
      ]);
      setCases(nextCases);
      setRuns(nextRuns);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('evaluation.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    if (!form) {
      return;
    }

    let cancelled = false;
    const handle = window.setTimeout(() => {
      listKnowledgeDocuments({ page: 1, pageSize: DOCUMENT_PICKER_PAGE_SIZE, search: documentSearch })
        .then(result => {
          if (!cancelled) {
            setDocuments(result.items);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setDocuments([]);
          }
        });
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [form, documentSearch]);

  const openCreate = () => {
    setEditingCaseId(null);
    setForm({ ...emptyForm });
    setDocumentSearch('');
  };

  const openEdit = (item: KnowledgeEvaluationCase) => {
    setEditingCaseId(item.id);
    setForm(toForm(item));
    setDocumentSearch('');
  };

  const closeForm = () => {
    setForm(null);
    setEditingCaseId(null);
  };

  const toggleDocument = (documentId: string) => {
    setForm(current => {
      if (!current) {
        return current;
      }
      const selected = current.expectedDocumentIds.includes(documentId);
      if (selected) {
        return { ...current, expectedDocumentIds: current.expectedDocumentIds.filter(id => id !== documentId) };
      }
      if (current.expectedDocumentIds.length >= MAX_EXPECTED_DOCUMENTS) {
        return current;
      }
      return { ...current, expectedDocumentIds: [...current.expectedDocumentIds, documentId] };
    });
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form) {
      return;
    }

    if (!form.question.trim()) {
      showToast(t('evaluation.validation.questionRequired'), 'error');
      return;
    }
    if (form.expectedDocumentIds.length === 0) {
      showToast(t('evaluation.validation.documentsRequired'), 'error');
      return;
    }

    setSaving(true);
    try {
      const request = toRequest(form);
      if (editingCaseId) {
        const updated = await updateKnowledgeEvaluationCase(editingCaseId, request);
        setCases(current => current.map(item => (item.id === updated.id ? updated : item)));
      } else {
        const created = await createKnowledgeEvaluationCase(request);
        setCases(current => [created, ...current]);
      }
      showToast(t('evaluation.saveSuccess'), 'success');
      closeForm();
    } catch (saveError) {
      showToast(saveError instanceof Error ? saveError.message : t('evaluation.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (item: KnowledgeEvaluationCase) => {
    if (!window.confirm(t('evaluation.confirmDelete'))) {
      return;
    }
    try {
      await deleteKnowledgeEvaluationCase(item.id);
      setCases(current => current.filter(entry => entry.id !== item.id));
      if (editingCaseId === item.id) {
        closeForm();
      }
      showToast(t('evaluation.deleteSuccess'), 'success');
    } catch (deleteError) {
      showToast(deleteError instanceof Error ? deleteError.message : t('evaluation.deleteFailed'), 'error');
    }
  };

  const onRun = async () => {
    setRunning(true);
    try {
      const run = await runKnowledgeEvaluation();
      setRuns(current => [run, ...current.filter(entry => entry.id !== run.id)].slice(0, 5));
      if (run.status === 'failed') {
        showToast(run.errorMessage ?? t('evaluation.runFailed'), 'error');
      } else {
        showToast(t('evaluation.runSuccess', { count: run.caseCount }), 'success');
      }
    } catch (runError) {
      showToast(runError instanceof Error ? runError.message : t('evaluation.runFailed'), 'error');
    } finally {
      setRunning(false);
    }
  };

  const activeCaseCount = cases.filter(item => item.isActive).length;
  const latestRun = runs.length > 0 ? runs[0] : null;
  const report = latestRun?.report ?? null;
  const variants = report?.variants ?? [];

  const best: Record<MetricKey, number | null> = {
    recallAtK: bestValueFor(variants, 'recallAtK', true),
    hitRate: bestValueFor(variants, 'hitRate', true),
    mrr: bestValueFor(variants, 'mrr', true),
    emptyRate: bestValueFor(variants, 'emptyRate', false),
    averageLatencyMilliseconds: bestValueFor(variants, 'averageLatencyMilliseconds', false),
    p95LatencyMilliseconds: bestValueFor(variants, 'p95LatencyMilliseconds', false),
  };

  const metricCell = (key: MetricKey, value: number, formatted: string) => (
    <td className={`numeric ${best[key] !== null && best[key] === value ? styles.bestValue : ''}`}>{formatted}</td>
  );

  const selectedDocumentTitles = React.useMemo(() => {
    if (!form) {
      return new Map<string, string>();
    }
    const map = new Map<string, string>();
    for (const doc of documents) {
      map.set(doc.id, doc.title);
    }
    const editing = editingCaseId ? cases.find(item => item.id === editingCaseId) : null;
    if (editing) {
      editing.expectedDocumentIds.forEach((id, index) => {
        if (!map.has(id)) {
          map.set(id, editing.expectedDocumentTitles[index] ?? id);
        }
      });
    }
    return map;
  }, [form, documents, cases, editingCaseId]);

  return (
    <section className="company-section" style={{ marginTop: 16 }}>
      <div className={styles.panelHeader}>
        <div>
          <h3>{t('evaluation.title')}</h3>
          <p className={styles.panelIntro}>{t('evaluation.intro')}</p>
        </div>
        <div className={styles.panelActions}>
          <button type="button" className="btn btn-secondary" onClick={() => void load()} disabled={loading || running}>
            {t('evaluation.refresh')}
          </button>
          <button type="button" className="btn btn-secondary" onClick={openCreate} disabled={loading || Boolean(form)}>
            {t('evaluation.addCase')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void onRun()}
            disabled={loading || running || activeCaseCount === 0}
            title={activeCaseCount === 0 ? t('evaluation.noActiveCases') : undefined}
          >
            {running ? t('evaluation.running') : t('evaluation.run')}
          </button>
        </div>
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}

      {loading ? (
        <Spinner text={t('evaluation.loading')} />
      ) : (
        <>
          {form ? (
            <form className={styles.form} onSubmit={event => void onSubmit(event)}>
              <div className={styles.field}>
                <label htmlFor="knowledge-eval-question">{t('evaluation.form.question')}</label>
                <input
                  id="knowledge-eval-question"
                  type="text"
                  maxLength={1000}
                  value={form.question}
                  onChange={event => setForm(current => (current ? { ...current, question: event.target.value } : current))}
                  placeholder={t('evaluation.form.questionPlaceholder')}
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="knowledge-eval-doc-search">{t('evaluation.form.expectedDocuments')}</label>
                <input
                  id="knowledge-eval-doc-search"
                  type="search"
                  value={documentSearch}
                  onChange={event => setDocumentSearch(event.target.value)}
                  placeholder={t('evaluation.form.documentSearchPlaceholder')}
                />
                <div className={styles.docPicker}>
                  {documents.length === 0 ? (
                    <div className={styles.docOptionEmpty}>{t('evaluation.form.noDocuments')}</div>
                  ) : (
                    documents.map(doc => {
                      const checked = form.expectedDocumentIds.includes(doc.id);
                      const disabled = !checked && form.expectedDocumentIds.length >= MAX_EXPECTED_DOCUMENTS;
                      return (
                        <label key={doc.id} className={styles.docOption}>
                          <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleDocument(doc.id)} />
                          <span>{doc.title}</span>
                        </label>
                      );
                    })
                  )}
                </div>
                <div className={styles.fieldHelp}>
                  {t('evaluation.form.selectedDocuments', {
                    count: form.expectedDocumentIds.length,
                    max: MAX_EXPECTED_DOCUMENTS,
                  })}
                  {form.expectedDocumentIds.length > 0
                    ? `: ${form.expectedDocumentIds.map(id => selectedDocumentTitles.get(id) ?? id).join(', ')}`
                    : ''}
                </div>
              </div>

              <div className={styles.field}>
                <label htmlFor="knowledge-eval-expected-text">{t('evaluation.form.expectedText')}</label>
                <textarea
                  id="knowledge-eval-expected-text"
                  maxLength={2000}
                  value={form.expectedText}
                  onChange={event => setForm(current => (current ? { ...current, expectedText: event.target.value } : current))}
                />
                <div className={styles.fieldHelp}>{t('evaluation.form.expectedTextHelp')}</div>
              </div>

              <div className={styles.field}>
                <label htmlFor="knowledge-eval-notes">{t('evaluation.form.notes')}</label>
                <textarea
                  id="knowledge-eval-notes"
                  maxLength={2000}
                  value={form.notes}
                  onChange={event => setForm(current => (current ? { ...current, notes: event.target.value } : current))}
                />
              </div>

              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={event => setForm(current => (current ? { ...current, isActive: event.target.checked } : current))}
                />
                <span>{t('evaluation.form.isActive')}</span>
              </label>

              <div className={styles.formActions}>
                <button type="button" className="btn btn-secondary" onClick={closeForm} disabled={saving}>
                  {t('evaluation.form.cancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? t('evaluation.form.saving') : t('evaluation.form.save')}
                </button>
              </div>
            </form>
          ) : null}

          <h4 className={styles.sectionTitle}>{t('evaluation.latestRun')}</h4>
          {!latestRun ? (
            <div className={styles.notice}>{t('evaluation.noRuns')}</div>
          ) : latestRun.status === 'failed' ? (
            <div className={styles.error}>
              {t('evaluation.runFailedAt', { at: formatDateTime(latestRun.startedAt) })}
              {latestRun.errorMessage ? `: ${latestRun.errorMessage}` : ''}
            </div>
          ) : !report ? (
            <div className={styles.notice}>{t('evaluation.runInProgress')}</div>
          ) : (
            <>
              <table className={styles.metricsTable}>
                <thead>
                  <tr>
                    <th>{t('evaluation.metrics.variant')}</th>
                    <th className="numeric">{t('evaluation.metrics.recallAtK', { k: report.k })}</th>
                    <th className="numeric">{t('evaluation.metrics.hitRate')}</th>
                    <th className="numeric">{t('evaluation.metrics.mrr')}</th>
                    <th className="numeric">{t('evaluation.metrics.emptyRate')}</th>
                    {variants.some(variant => variant.textMatchRate !== null) ? (
                      <th className="numeric">{t('evaluation.metrics.textMatchRate')}</th>
                    ) : null}
                    <th className="numeric">{t('evaluation.metrics.latency')}</th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map(variant => (
                    <tr key={variant.variant}>
                      <td>{t(`evaluation.variants.${variant.variant}`, { defaultValue: variant.label })}</td>
                      {metricCell('recallAtK', variant.recallAtK, formatPercent(variant.recallAtK))}
                      {metricCell('hitRate', variant.hitRate, formatPercent(variant.hitRate))}
                      {metricCell('mrr', variant.mrr, formatScore(variant.mrr))}
                      {metricCell('emptyRate', variant.emptyRate, formatPercent(variant.emptyRate))}
                      {variants.some(entry => entry.textMatchRate !== null) ? (
                        <td className="numeric">{variant.textMatchRate === null ? '—' : formatPercent(variant.textMatchRate)}</td>
                      ) : null}
                      {metricCell(
                        'averageLatencyMilliseconds',
                        variant.averageLatencyMilliseconds,
                        `${formatNumber(variant.averageLatencyMilliseconds)} / ${formatNumber(variant.p95LatencyMilliseconds)} ms`,
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className={styles.runSummary}>
                {t('evaluation.runSummary', {
                  at: formatDateTime(latestRun.completedAt ?? latestRun.startedAt),
                  count: report.caseCount,
                })}
                {' · '}
                <button
                  type="button"
                  className="btn btn-link"
                  style={{ padding: 0, minHeight: 0, fontSize: 'inherit' }}
                  onClick={() => setShowCaseDetails(current => !current)}
                >
                  {showCaseDetails ? t('evaluation.hideCaseDetails') : t('evaluation.showCaseDetails')}
                </button>
              </div>

              {showCaseDetails ? (
                <table className={styles.casesTable} style={{ marginTop: 8 }}>
                  <thead>
                    <tr>
                      <th>{t('evaluation.form.question')}</th>
                      {variants.map(variant => (
                        <th key={variant.variant} className="numeric">
                          {t(`evaluation.variants.${variant.variant}`, { defaultValue: variant.label })}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.cases.map(item => (
                      <tr key={item.caseId}>
                        <td className={styles.caseQuestion}>{item.question}</td>
                        {variants.map(variant => {
                          const result = item.variants[variant.variant];
                          if (!result) {
                            return <td key={variant.variant} className="numeric">—</td>;
                          }
                          return (
                            <td key={variant.variant} className="numeric" title={result.method}>
                              {result.empty
                                ? t('evaluation.caseResult.empty')
                                : result.firstRelevantRank
                                  ? t('evaluation.caseResult.rank', { rank: result.firstRelevantRank })
                                  : t('evaluation.caseResult.miss')}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </>
          )}

          <h4 className={styles.sectionTitle}>{t('evaluation.cases', { count: cases.length, active: activeCaseCount })}</h4>
          {cases.length === 0 ? (
            <div className={styles.notice}>{t('evaluation.noCases')}</div>
          ) : (
            <table className={styles.casesTable}>
              <tbody>
                {cases.map(item => (
                  <tr key={item.id}>
                    <td>
                      <div className={styles.caseQuestion}>
                        {item.question}
                        {!item.isActive ? <span className={styles.inactiveBadge}>{t('evaluation.inactive')}</span> : null}
                      </div>
                      <div className={styles.caseMeta}>
                        {t('evaluation.expects', { documents: item.expectedDocumentTitles.join(', ') })}
                      </div>
                    </td>
                    <td>
                      <div className={styles.rowActions}>
                        <button type="button" className="btn btn-secondary" onClick={() => openEdit(item)} disabled={saving}>
                          {t('evaluation.edit')}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={() => void onDelete(item)} disabled={saving}>
                          {t('evaluation.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
};

export default KnowledgeEvaluationPanel;
